import React, { useEffect } from 'react';
import BRAND, { applyBrandTheme } from '../brandConfig';

// The QPS Hub Technician Agreement, at /vendor-terms.
//
// Opens without a login, because a technician reads it before they have an
// account and sends it to whoever advises them. Linked from the tick box they
// accept it with, and from the Payments tab.
//
// Written in the plainest English the subject allows. The people signing it
// are tradespeople, many of them reading in a second language, on a phone, in
// a truck. An agreement nobody understands is not an agreement, and a clause
// that only makes sense to a lawyer is a clause QPS Hub cannot fairly enforce.
//
// Every number and mechanism here is what the code actually does. When the
// code changes, this changes, and VENDOR_TERMS_VERSION in
// server/lib/vendorTerms.js changes with it.
export const VENDOR_TERMS_VERSION = '2026-10-07';
export const VENDOR_TERMS_EFFECTIVE = 'October 7, 2026';

const S = ({ n, title, children }) => (
  <section className="vt-sec">
    <h2 className="vt-h"><span className="vt-n">{n}</span>{title}</h2>
    {children}
  </section>
);

export default function VendorTerms() {
  useEffect(() => { applyBrandTheme(); }, []);

  return (
    <div className="vt-page">
      <div className="vt-sheet">
        <header className="vt-head">
          <div className="vt-brand">QPS HUB</div>
          <h1 className="vt-title">Technician Agreement</h1>
          <p className="vt-meta">Version {VENDOR_TERMS_VERSION} · In effect from {VENDOR_TERMS_EFFECTIVE}</p>
        </header>

        <p className="vt-lead">
          This is the agreement between you and Quick Property Services LLC, which runs QPS Hub. It covers what QPS
          Hub does for you, what it costs, and who is responsible for what. Read it before you tick the box.
        </p>

        <S n="1" title="What QPS Hub is">
          <p>
            QPS Hub is software. You use it to write estimates and invoices for your own customers, send them, and
            get paid online.
          </p>
          <p>
            When you work for your own customer, the agreement for that work is between you and them. QPS Hub is not
            a party to it. We do not set your prices, do your work, guarantee it, or stand behind it. You do.
          </p>
          <p>
            You are an independent business. You are not an employee, partner or agent of Quick Property Services LLC,
            and nothing here makes you one.
          </p>
        </S>

        <S n="2" title="What it costs">
          <p>
            Nothing to join. No monthly fee. Writing and sending estimates and invoices is free, however many you send.
          </p>
          <p>
            <strong>When a customer pays you online through QPS Hub, we keep 10% of the total that customer pays.</strong>
            {' '}That is the whole cost of using QPS Hub.
          </p>
          <p>
            We do not charge the 10% on work your customer pays for any other way. Cash, check, their own card
            machine, a bank transfer they send you directly: none of it goes through us, and none of it is charged.
          </p>
        </S>

        <S n="3" title="How the price is worked out">
          <p>
            You type your price. Your customer is charged your price, plus what the card or bank network charges to
            process the payment, plus the 10% QPS Hub fee. Your customer sees one total, with no fee lines on it.
          </p>
          <p>On a $1,000 invoice, as the rates stand today:</p>
          <table className="vt-table">
            <tbody>
              <tr><td>Your customer pays</td><td>$1,148.45</td></tr>
              <tr><td>You receive</td><td>$1,000.00</td></tr>
              <tr><td>QPS Hub keeps</td><td>$114.85</td></tr>
              <tr><td>Covers payment processing</td><td>$33.60</td></tr>
            </tbody>
          </table>
          <p>
            <strong>You receive the price you typed.</strong> The fee and the processing are added on top of it, not
            taken out of it.
          </p>
          <p>
            Every estimate and invoice stores the rates it was written with. If we change a rate later, documents
            already written keep the price your customer already agreed to.
          </p>
        </S>

        <S n="4" title="Getting paid">
          <p>
            Payments run through Stripe. You connect a Stripe account through QPS Hub, and Stripe asks for your ID
            and your bank account so it knows who it is sending money to. Stripe holds those details. QPS Hub never
            sees or stores your bank account number.
          </p>
          <p>
            When your customer pays, Stripe splits that one payment on the spot: your share goes to your Stripe
            account, our 10% goes to ours. Stripe then pays your share into your bank on its own schedule. Your money
            never sits in a Quick Property Services bank account, and you never wait on us to send it.
          </p>
          <p>
            A card payment settles within about a day. A bank transfer takes three to five business days to clear, and
            shows as Processing until it does.
          </p>
          <p>
            Connecting Stripe is optional. Without it your customers simply cannot pay you online, and you bill them
            however you did before.
          </p>
        </S>

        <S n="5" title="If a customer disputes a payment">
          <p>
            A customer is able to tell their bank to reverse a payment they already made. That is called a chargeback,
            and the bank decides it, not us and not you.
          </p>
          <p><strong>Nothing is taken from you while the bank is deciding.</strong></p>
          <p>
            We will email you the same day it starts and tell you what the customer claimed. Send us your photos of
            the finished work, the estimate they approved, their signature, and any messages between you. Send them
            quickly. Banks set a deadline, and evidence that arrives after it counts for nothing.
          </p>
          <p>
            <strong>If the bank sides with you</strong>, nothing changes and the money stays yours.
          </p>
          <p>
            <strong>If the bank sides with your customer</strong>, the money goes back to them, and your share comes
            back out of your Stripe balance along with the fee the bank charges for the dispute. It was your customer
            and your work, so it is your cost. Stripe takes it out of your next job first. If you have no balance,
            your Stripe account shows a negative figure until a job covers it, and Stripe may collect it from the bank
            account you gave them.
          </p>
          <p>Bank decisions on chargebacks are final. There is no second appeal.</p>
        </S>

        <S n="6" title="Refunds">
          <p>
            Whether to refund a customer is your decision, the same as it was before you used QPS Hub. Tell us and we
            will process it. A refund comes back out of your share, and the 10% on that payment comes back out of ours.
          </p>
        </S>

        <S n="7" title="What you are responsible for">
          <ul className="vt-list">
            <li>Holding whatever licence, insurance and workers compensation cover your work and the state of Utah require, and keeping them current with us.</li>
            <li>The work itself: doing it, standing behind it, and sorting out anything that goes wrong with your customer.</li>
            <li>Invoicing honestly, for work actually done, at the price your customer agreed to.</li>
            <li>Your own taxes. We do not withhold anything. Your Stripe account holds your payout records.</li>
            <li>Keeping your login to yourself. Anything done from your account is treated as done by you.</li>
          </ul>
        </S>

        <S n="8" title="What we are responsible for">
          <ul className="vt-list">
            <li>Running QPS Hub, and splitting every online payment the way section 3 describes.</li>
            <li>Showing you every payment with the 10% and the processing spelled out line by line, so you can check our arithmetic against your bank.</li>
            <li>Telling you the same day a customer disputes a payment, and helping you fight it.</li>
            <li>Giving you thirty days notice before any rate in section 3 goes up.</li>
            <li>Keeping your documents and your customer list to ourselves. Your customers are yours.</li>
          </ul>
          <p>
            QPS Hub is provided as it is. We do not promise it will never be down, and we are not liable for business
            you lose while it is. Where we are liable for something, that liability is limited to the platform fees you
            paid us in the three months before it happened.
          </p>
        </S>

        <S n="9" title="Ending this agreement">
          <p>
            You can stop using QPS Hub whenever you like. We can end your access if you break this agreement, invoice
            for work you did not do, let your insurance or licence lapse, or cost us money we cannot recover.
          </p>
          <p>
            Ending it does not cancel money already moving. Payments in flight still settle, chargebacks already
            started are still yours, and anything you already owe is still owed. Your documents stay available to you
            for at least ninety days after you leave.
          </p>
        </S>

        <S n="10" title="Changes to this agreement">
          <p>
            We will tell you before this agreement changes, and the version number and date at the top will change
            with it. Using QPS Hub after that is how you accept the new version. If you would rather not, stop using
            it, and section 9 applies.
          </p>
        </S>

        <S n="11" title="The legal part">
          <p>
            This agreement is governed by the law of the State of Utah. Any dispute between us is handled in the
            courts of Utah County, Utah.
          </p>
          <p>
            If any part of this agreement turns out to be unenforceable, the rest of it still stands.
          </p>
        </S>

        <footer className="vt-foot">
          <p>
            Quick Property Services LLC · {BRAND.serviceArea.regionLabel} · {BRAND.company.website}
          </p>
          <p className="vt-ask">
            Anything here you are unsure about, ask us before you tick the box. We would rather explain it twice than
            argue about it once.
          </p>
        </footer>
      </div>
    </div>
  );
}
